/*--External-- */
import _ from "lodash";
import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";

/**--Internal-- */
import UIModal from "UIComponents/UIModal";
import CaptureVideo from "Post/routes/CreatePost/components/Screens/CaptureVideo";
import FullScreenLoader from "UIComponents/FullScreenLoader";

/**--Relative--*/
import ShowQuestionDetail from "./ShowQuestionDetail";
import { uploadAndCreateAttachment } from "../VideoResponseFieldUtils";

/**
 *
 * @param {*} params
 * @returns Video Recording Component
 */
const RecordVideoResponse = ({
  t,
  mode,
  parentId,
  closeModal,
  questionId,
  resourceId,
  uploadFileV2,
  worksheetMode,
  isSubQuestion,
  questionIndex,
  setIsUploading,
  updateAttachments,
  videoDurationLimit,
  subQuestionParentId,
  isFromInternalTool = false,
  makeAutoSubmitOnTimesUp = false,
  updateIsQuestionTheaterModeEnable = _.noop,
  isQueriesV2Enabled,
}) => {
  const [showLoader, setShowLoader] = useState(false);

  // * Callback to handle the completion of video recording
  const onRecordingComplete = useCallback(
    async data => {
      const recordings = _.get(data, "attachments", []);
      if (_.isEmpty(recordings)) return;

      if (makeAutoSubmitOnTimesUp) {
        setShowLoader(true);
      } else {
        closeModal?.();
        setIsUploading?.(true);
      }

      // * Uploading the video and creating an attachment
      const recording = _.first(recordings);
      const attachment = await uploadAndCreateAttachment({
        mode,
        parentId,
        uploadFileV2,
        attachment: recording,
      });

      updateIsQuestionTheaterModeEnable(false);
      setShowLoader(false);
      closeModal?.();

      // * If no attachment ID is returned, stop uploading
      if (!attachment) {
        setIsUploading?.(false);
        return;
      }

      // * Saving attachment ID to the question field
      updateAttachments([attachment]);
    },
    [
      closeModal,
      parentId,
      setIsUploading,
      uploadFileV2,
      updateAttachments,
      makeAutoSubmitOnTimesUp,
    ]
  );

  const config = {
    resourceId,
    questionId,
    isSubQuestion,
    questionIndex,
    subQuestionParentId,
  };

  if (showLoader) return <FullScreenLoader />;

  return (
    <UIModal isOpen isFullScreen>
      <CaptureVideo
        showDesignV2={true}
        hideGlobalTray={true}
        recordingTimeLimit={videoDurationLimit}
        screenPanelProps={{
          updatePost: onRecordingComplete,
          postDetails: { attachments: [] },
          onCloseClick: () => closeModal?.(),
        }}
        rightHeaderButton={
          <ShowQuestionDetail
            t={t}
            config={config}
            worksheetMode={worksheetMode}
            isFromInternalTool={isFromInternalTool}
            isQueriesV2Enabled={isQueriesV2Enabled}
          />
        }
        shouldBeResponsive={true}
        canAutoSubmitNow={makeAutoSubmitOnTimesUp}
      />
    </UIModal>
  );
};

RecordVideoResponse.propTypes = {
  t: PropTypes.func,
  parentId: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  uploadFileV2: PropTypes.func.isRequired,
  setIsUploading: PropTypes.func.isRequired,
  updateAttachments: PropTypes.func.isRequired,
  videoDurationLimit: PropTypes.number,
  isQueriesV2Enabled: PropTypes.bool,
};

RecordVideoResponse.defaultProps = {
  t: _.noop,
  parentId: "",
  closeModal: _.noop,
  uploadFileV2: _.noop,
  setIsUploading: _.noop,
  updateAttachments: _.noop,
  videoDurationLimit: 600000,
  isQueriesV2Enabled: false,
};

export default RecordVideoResponse;
